/**
 * Single preset item row - checkbox, drag handle, expandable full edit
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Fragment, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { DEFAULT_CLICK_ID_STRUCTURE, DEFAULT_DOMAIN_SCHEME } from '@trafficpulse/shared';

import { Checkbox, Tooltip } from '@/components/ui';
import { StaticTagBadge } from '@/features/smswiser-campaigns/components/drop-modals';
import { useStaticTags } from '@/hooks';
import { cn } from '@/lib/utils';
import type { EditableItem } from './useEditPresetForm';
import type { PresetsModalCatalog } from '../shared';

import { EditPresetItemExpanded } from './EditPresetItemExpanded';

const CELL = 'truncate py-2 pr-2 text-sm';
const CELL_MUTED = `${CELL} text-muted-foreground`;
const CELL_NUM = 'py-2 pr-2 text-right text-sm tabular-nums';

/** "Https · Subdomain" — only the non-default shortlink options, null when none. */
function shortlinkHint(item: EditableItem): string | null {
  const parts: string[] = [];
  if (item.domainScheme && item.domainScheme !== DEFAULT_DOMAIN_SCHEME) parts.push('Https');
  if (item.clickIdStructure && item.clickIdStructure !== DEFAULT_CLICK_ID_STRUCTURE) parts.push('Subdomain');
  return parts.length ? parts.join(' · ') : null;
}

function DropRowTooltipContent({
  campaign,
  gateway,
  leads,
  amount,
  tld,
  tag,
}: {
  campaign: string;
  gateway: string;
  leads: string;
  amount: number;
  tld: string;
  tag: { name: string; color: string } | null;
}) {
  return (
    <div className="flex flex-col gap-1 text-left">
      <div><span className="opacity-80">Campaign:</span> {campaign}</div>
      <div><span className="opacity-80">Gateway:</span> {gateway}</div>
      <div><span className="opacity-80">Leads:</span> {leads}</div>
      <div><span className="opacity-80">Amount:</span> {amount}</div>
      <div><span className="opacity-80">TLD:</span> {tld}</div>
      <div className="flex items-center gap-1.5">
        <span className="opacity-80">Static tag:</span>
        {tag ? <StaticTagBadge name={tag.name} color={tag.color} /> : <span>—</span>}
      </div>
    </div>
  );
}

export interface EditPresetItemRowProps {
  item: EditableItem;
  index: number;
  catalog: PresetsModalCatalog;
  isSelected: boolean;
  onToggleSelect: () => void;
  onUpdate: (patch: Partial<EditableItem>) => void;
}

export const EditPresetItemRow: FC<EditPresetItemRowProps> = ({
  item,
  index,
  catalog,
  isSelected,
  onToggleSelect,
  onUpdate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const { data: staticTags } = useStaticTags();
  const campaign = catalog.campaigns.find((c) => c.id === item.campaignWiserId);
  const gateway = catalog.gateways.find((g) => g.id === item.routeId);
  const leads = catalog.leadsLists.find((l) => l.id === item.leadsListWiserId);
  const tld = catalog.tlds.find((t) => t.value === item.shortLinkTldValue);
  const tag = item.staticTagId ? staticTags?.find((t) => t.id === item.staticTagId) : null;
  const hint = shortlinkHint(item);

  const fullSummary = (
    <DropRowTooltipContent
      campaign={campaign?.name ?? '—'}
      gateway={gateway?.name ?? '—'}
      leads={leads?.name ?? '—'}
      amount={item.amount}
      tld={tld?.displayName ?? item.shortLinkTldValue ?? '—'}
      tag={tag ? { name: tag.name, color: tag.color || '#6b7280' } : null}
    />
  );

  const row = (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'grid cursor-pointer grid-cols-[2.5rem_2.5rem_2.5rem_3rem_1fr_1fr_1fr_3.5rem_1fr_minmax(0,1fr)] items-center gap-3 border-b border-border/40 bg-muted/5 px-3 hover:bg-muted/15',
        isDragging && 'opacity-50',
      )}
      onClick={() => setExpanded((e) => !e)}
    >
      <span className="flex justify-center" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} aria-label={`Select item ${index + 1}`} />
      </span>
      <div
        className="flex justify-center touch-none cursor-grab text-muted-foreground hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <span className="flex justify-center text-muted-foreground">
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </span>
      <span className={cn(CELL_MUTED, 'w-12')}>#{index + 1}</span>
      <span className={CELL}>{campaign?.name ?? '—'}</span>
      <span className={CELL_MUTED}>{gateway?.name ?? '—'}</span>
      <span className={CELL_MUTED}>{leads?.name ?? '—'}</span>
      <span className={CELL_NUM}>{item.amount}</span>
      <span className={CELL_MUTED}>
        {tld?.displayName ?? item.shortLinkTldValue ?? '—'}
        {hint && <span className="ml-1.5 text-[10px] text-primary/80">{hint}</span>}
      </span>
      <span className={CELL}>
        {tag ? (
          <span
            className="inline-block max-w-full truncate rounded-md border border-transparent px-2 py-0.5 text-xs font-medium text-white"
            style={{ backgroundColor: tag.color || '#6b7280' }}
          >
            {tag.name}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </span>
    </div>
  );

  return (
    <Fragment>
      <Tooltip content={fullSummary} contentClassName="max-w-sm" className="block w-full">
        {row}
      </Tooltip>
      {expanded && !isDragging && (
        <div className="border-b border-border/40 bg-muted/5">
          <EditPresetItemExpanded item={item} catalog={catalog} onUpdate={onUpdate} />
        </div>
      )}
    </Fragment>
  );
};
